import { Cron } from "croner";
import * as CronParser from "cron-parser";
import CronGenerator from "cron-time-generator";

import {
  IntervalUnit,
  ScheduleType,
  type IntervalSchedule,
} from "../sync/scheduler/schedule-types";
import {
  campaignScheduleConfigSchema,
  resolveScheduleEndMs,
  resolveScheduleStartMs,
  type TimezoneMode,
} from "./types/config-types";

// This module is imported by the frontend app (next-run display) as well as
// the scheduler, so it must stay free of node-only imports.

/**
 * Max UTC offset in hours (UTC+14). Recipient-local campaigns anchor here so the
 * earliest timezone is eligible when the run is created.
 */
export const MAX_UTC_OFFSET_HOURS = 14;

/**
 * Override a 5-field cron's hour/minute with the marketer's wall-clock `HH:mm`
 * (CEP owns time-of-day on `schedule.startTime`; the cron's is vestigial).
 * Returns the input unchanged if there's no override or it isn't 5 fields.
 */
export function applyStartTimeToCron(
  expression: string,
  startTime: string | undefined,
): string {
  if (!startTime) return expression;
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) return expression;
  const timeParts = startTime.split(":");
  if (timeParts.length !== 2 || !timeParts[0] || !timeParts[1]) {
    return expression;
  }
  const [hh, mi] = timeParts.map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mi)) return expression;
  return `${mi} ${hh} ${parts[2]} ${parts[3]} ${parts[4]}`;
}

/**
 * Evaluate a cron expression in a named IANA timezone (DST-aware).
 * "Friday 9am" with timezone="America/New_York" fires at Friday 9am ET.
 */
export function evaluateCronInTimezone(
  expression: string,
  lowerBound: Date,
  endDate: Date | string,
  timezone: string,
): boolean {
  const currentDate = new Date();

  if (!expression.includes("?")) {
    const job = Cron(expression.trim(), {
      timezone,
      startAt: lowerBound,
      stopAt: endDate,
    });
    const nextScheduled = job.nextRun(lowerBound);
    return Boolean(nextScheduled && nextScheduled <= currentDate);
  }

  const exp = CronParser.parseExpression(expression, {
    currentDate,
    endDate,
    tz: timezone,
  });
  const lastScheduledRun = exp.prev().toDate();
  return lastScheduledRun > lowerBound;
}

/**
 * Evaluate a cron with a fixed UTC offset (minutes) for the recipient-local
 * UTC+14 anchor. cron-parser needs a named tz, so we map whole-hour offsets to
 * `Etc/GMT-N`.
 */
export function evaluateCronWithOffset(
  expression: string,
  lowerBound: Date,
  endDate: Date | string,
  utcOffsetMinutes: number,
): boolean {
  const currentDate = new Date();

  if (!expression.includes("?")) {
    const job = Cron(expression.trim(), {
      utcOffset: utcOffsetMinutes,
      startAt: lowerBound,
      stopAt: endDate,
    });
    const nextScheduled = job.nextRun(lowerBound);
    return Boolean(nextScheduled && nextScheduled <= currentDate);
  }

  // POSIX-style: Etc/GMT-N means UTC+N (whole-hour offsets only).
  const offsetHours = utcOffsetMinutes / 60;
  const exp = CronParser.parseExpression(expression, {
    currentDate,
    endDate,
    tz: `Etc/GMT-${offsetHours}`,
  });
  const lastScheduledRun = exp.prev().toDate();
  return lastScheduledRun > lowerBound;
}

export type VisualCronExpressionInput = {
  time?: string;
  days: Record<string, boolean | undefined>;
};

/**
 * Evaluate a visual cron schedule in a named IANA timezone. `overrideTime`
 * (HH:mm) replaces every expression's persisted `time` (vestigial when set).
 */
export function evaluateVisualCronInTimezone(
  expressions: VisualCronExpressionInput[],
  lowerBound: Date,
  endDate: Date | string,
  timezone: string,
  overrideTime?: string,
): boolean {
  return Boolean(
    expressions.find((exp) => {
      const time = overrideTime ?? exp.time;
      if (!time) return false;
      // NaN (not a skip) on malformed time so the cron evaluator throws.
      const [hours = Number.NaN, minutes = Number.NaN] = time
        .split(":")
        .map(Number);
      const days = Object.keys(exp.days).filter((key) => exp.days[key]);
      if (days.length === 0) {
        return false;
      }
      const expression = CronGenerator.onSpecificDaysAt(days, hours, minutes);
      return evaluateCronInTimezone(expression, lowerBound, endDate, timezone);
    }),
  );
}

/** Evaluate a visual cron schedule with a fixed UTC offset. See `evaluateVisualCronInTimezone`. */
export function evaluateVisualCronWithOffset(
  expressions: VisualCronExpressionInput[],
  lowerBound: Date,
  endDate: Date | string,
  utcOffsetMinutes: number,
  overrideTime?: string,
): boolean {
  return Boolean(
    expressions.find((exp) => {
      const time = overrideTime ?? exp.time;
      if (!time) return false;
      // NaN (not a skip) on malformed time so the cron evaluator throws.
      const [hours = Number.NaN, minutes = Number.NaN] = time
        .split(":")
        .map(Number);
      const days = Object.keys(exp.days).filter((key) => exp.days[key]);
      if (days.length === 0) {
        return false;
      }
      const expression = CronGenerator.onSpecificDaysAt(days, hours, minutes);
      return evaluateCronWithOffset(
        expression,
        lowerBound,
        endDate,
        utcOffsetMinutes,
      );
    }),
  );
}

/**
 * Structural input for `getNextCampaignRunTime` satisfied by both the backend's
 * `CepScheduleConfig` and the app's `Schedule`. `schedule` is the raw jsonb,
 * narrowed internally per `type`.
 */
export type CampaignRecurringScheduleInput = {
  type: string;
  schedule?: unknown;
  startDate?: string | null;
  startTime?: string;
  timezone?: string;
  endDate?: string | null;
};

const FAR_FUTURE_STOP = "2100-01-01T00:00:00Z";

/**
 * Next fire instant of a recurring cron/visual-cron schedule — forward-looking
 * analog of the scheduler's `shouldRecurringCampaignRun`, sharing its semantics:
 * time-of-day from `startTime`; fixed mode in `schedule.timezone` (default UTC),
 * recipient_local at UTC+14; suppressed before `startDate`, stops at `endDate`.
 *
 * Returns `null` for non-recurring/expired/expression-less schedules. Cron parse
 * errors are not caught — the scheduler wants loud failures; display callers wrap.
 */
export function getNextCampaignRunTime({
  schedule,
  timezoneMode,
  now = new Date(),
}: {
  schedule: CampaignRecurringScheduleInput;
  timezoneMode: TimezoneMode;
  now?: Date;
}): Date | null {
  if (schedule.type !== "cron" && schedule.type !== "visual_cron") {
    return null;
  }

  const tzName =
    timezoneMode === "recipient_local"
      ? "Etc/GMT-14"
      : (schedule.timezone ?? "UTC");

  const endMs = resolveScheduleEndMs(schedule.endDate ?? undefined, tzName);
  if (endMs !== null && endMs < now.getTime()) {
    return null;
  }
  const stopAt =
    endMs !== null ? new Date(endMs).toISOString() : FAR_FUTURE_STOP;

  // First fire is the next cron occurrence at or after `startDate`. Croner's
  // `nextRun` is exclusive, so back the anchor off 1ms to include an occurrence
  // landing exactly on it (the common case: first fire IS startDate + startTime).
  const startMs = resolveScheduleStartMs(
    {
      startDate: schedule.startDate ?? undefined,
      startTime: schedule.startTime,
    },
    tzName,
  );
  const lowerBound =
    startMs !== null && startMs > now.getTime() ? new Date(startMs - 1) : now;

  const cronOptions =
    timezoneMode === "recipient_local"
      ? { utcOffset: MAX_UTC_OFFSET_HOURS * 60, stopAt }
      : { timezone: tzName, stopAt };

  if (schedule.type === "cron") {
    const cronSchedule = schedule.schedule as
      | { expression?: string }
      | undefined;
    const expression = cronSchedule?.expression;
    if (!expression) return null;
    const job = Cron(
      applyStartTimeToCron(expression, schedule.startTime).trim(),
      cronOptions,
    );
    return job.nextRun(lowerBound);
  }

  const visualSchedule = schedule.schedule as
    | { expressions?: Partial<VisualCronExpressionInput>[] }
    | undefined;
  const expressions = visualSchedule?.expressions;
  if (!expressions?.length) return null;

  // Next run is the minimum next occurrence across expressions.
  let earliest: Date | null = null;
  for (const exp of expressions) {
    const time = schedule.startTime ?? exp.time;
    if (!time || !exp.days) continue;
    // NaN (not a skip) on malformed time so croner throws.
    const [hours = Number.NaN, minutes = Number.NaN] = time
      .split(":")
      .map(Number);
    const dayMap = exp.days;
    const days = Object.keys(dayMap).filter((key) => dayMap[key]);
    if (days.length === 0) continue;
    const expression = CronGenerator.onSpecificDaysAt(days, hours, minutes);
    const next = Cron(expression, cronOptions).nextRun(lowerBound);
    if (next && (earliest === null || next < earliest)) {
      earliest = next;
    }
  }
  return earliest;
}

export type InAppScheduleWindowPosition = "before" | "inside" | "after";

/**
 * Where `now` falls relative to an in-app campaign's `[startDate, endDate)`
 * window, resolved in the campaign's timezone (or UTC). recipient_local has no
 * meaning for in-app, so the timezone mode is ignored. A missing bound is open.
 */
export function resolveInAppScheduleWindow(
  schedule: {
    startDate?: string | null;
    startTime?: string;
    endDate?: string | null;
    timezone?: string;
  },
  now: number,
): InAppScheduleWindowPosition {
  const tz = schedule.timezone ?? "UTC";

  const startMs = resolveScheduleStartMs(
    {
      startDate: schedule.startDate ?? undefined,
      startTime: schedule.startTime,
    },
    tz,
  );
  if (startMs !== null && now < startMs) return "before";

  const endMs = resolveScheduleEndMs(schedule.endDate ?? undefined, tz);
  if (endMs !== null && now >= endMs) return "after";

  return "inside";
}

/**
 * The interval an in-app campaign reconciles on, or null when its schedule is
 * not a usable interval schedule. An in-app campaign reconciles only on an
 * interval, so null means nothing should start a reconcile for it. The value
 * comes from a jsonb column, so its shape is checked rather than trusted.
 */
export function getInAppReconcileInterval(schedule: {
  type?: string;
  schedule?: unknown;
}): IntervalSchedule | null {
  if (schedule.type !== ScheduleType.Interval) return null;
  const inner = schedule.schedule;
  if (typeof inner !== "object" || inner === null) return null;
  const interval = (inner as { interval?: unknown }).interval;
  if (typeof interval !== "object" || interval === null) return null;
  const { unit, quantity } = interval as { unit?: unknown; quantity?: unknown };
  if (
    typeof quantity !== "number" ||
    !Object.values<unknown>(IntervalUnit).includes(unit)
  ) {
    return null;
  }
  return { interval: { unit: unit as IntervalUnit, quantity } };
}

const INTERVAL_UNIT_MS: Record<IntervalUnit, number> = {
  [IntervalUnit.Minute]: 60_000,
  [IntervalUnit.Hour]: 3_600_000,
  [IntervalUnit.Day]: 86_400_000,
  [IntervalUnit.Week]: 604_800_000,
};

/** The in-app reconcile interval in milliseconds, or null when the schedule has none. */
export function getInAppReconcileIntervalMs(schedule: {
  type?: string;
  schedule?: unknown;
}): number | null {
  const intervalSchedule = getInAppReconcileInterval(schedule);
  if (!intervalSchedule) return null;
  const { quantity, unit } = intervalSchedule.interval;
  const ms = quantity * INTERVAL_UNIT_MS[unit];
  return ms > 0 ? ms : null;
}

/**
 * The reconcile slot an in-app campaign is due for at `now`, or null when none
 * is. Slots fall a whole number of intervals after the last slot that ran, so
 * a late scheduler tick never pushes later slots back. A slot that arrived
 * before the previous run completed is skipped rather than run late. The first
 * run's slot is the latest one on the grid that starts at `firstSlotAt`.
 */
export function dueInAppReconcileSlot({
  intervalMs,
  lastSlotAt,
  previousRunCompletedAt,
  firstSlotAt,
  now,
}: {
  intervalMs: number;
  lastSlotAt: Date | null;
  previousRunCompletedAt: Date | null;
  firstSlotAt: number | null;
  now: number;
}): Date | null {
  if (!lastSlotAt) {
    if (firstSlotAt === null || firstSlotAt > now) return new Date(now);
    return new Date(latestSlotAtOrBefore(firstSlotAt, intervalMs, now));
  }
  const last = lastSlotAt.getTime();
  if (now - last < intervalMs) return null;
  const slot = latestSlotAtOrBefore(last, intervalMs, now);
  if (previousRunCompletedAt && previousRunCompletedAt.getTime() > slot) {
    return null;
  }
  return new Date(slot);
}

/**
 * The next slot `dueInAppReconcileSlot` will run, given the last slot that ran
 * and when that run completed: the first slot after `lastSlotAt` that the run
 * did not overlap.
 */
export function nextInAppReconcileSlot({
  intervalMs,
  lastSlotAt,
  previousRunCompletedAt,
}: {
  intervalMs: number;
  lastSlotAt: Date;
  previousRunCompletedAt: Date | null;
}): Date {
  const last = lastSlotAt.getTime();
  const completed = previousRunCompletedAt?.getTime() ?? last;
  const intervals = Math.max(1, Math.ceil((completed - last) / intervalMs));
  return new Date(last + intervals * intervalMs);
}

function latestSlotAtOrBefore(
  anchor: number,
  intervalMs: number,
  now: number,
): number {
  return anchor + Math.floor((now - anchor) / intervalMs) * intervalMs;
}

/** Recipient-local schedule: recurring `cron`/`visual_cron` or one-time `manual`. */
export type RecipientLocalSchedule =
  | { kind: "cron"; sendHour: number; sendMinute: number }
  | { kind: "specific"; localDate: string; localTime: string };

/**
 * Per-recipient send time of a recipient_local campaign. Returns null when the
 * schedule has none, so activation can block it and the orchestrator can fail fast.
 */
export function readRecipientLocalSchedule(
  scheduleConfig: unknown,
): RecipientLocalSchedule | null {
  const parsed = campaignScheduleConfigSchema.safeParse(scheduleConfig);
  if (!parsed.success) return null;
  const cfg = parsed.data;

  // startTime overrides the schedule's own HH:MM, matching the scheduler's
  // evaluation in sync/scheduler/batch-campaigns.ts.
  const scheduleTime =
    cfg.schedule?.expressions?.[0]?.time ??
    cronExpressionTime(cfg.schedule?.expression);
  const cronTime = scheduleTime && (cfg.startTime ?? scheduleTime);
  if (cronTime) {
    const [hourStr, minuteStr] = cronTime.split(":");
    const sendHour = Number(hourStr);
    const sendMinute = minuteStr === undefined ? 0 : Number(minuteStr);
    if (
      !Number.isInteger(sendHour) ||
      sendHour < 0 ||
      sendHour > 23 ||
      !Number.isInteger(sendMinute) ||
      sendMinute < 0 ||
      sendMinute > 59
    ) {
      throw new Error(
        `Invalid cron time "${cronTime}" for recipient_local campaign — expected HH:MM`,
      );
    }
    return { kind: "cron", sendHour, sendMinute };
  }

  if (cfg.type === "manual") {
    // A legacy ISO-instant startDate (has a `T`) is not a wall-clock date.
    const wallClockStartDate = cfg.startDate?.includes("T")
      ? undefined
      : cfg.startDate;
    const localDate = cfg.localDate ?? wallClockStartDate;
    const localTime = cfg.localTime ?? cfg.startTime;
    if (localDate && localTime) {
      return { kind: "specific", localDate, localTime };
    }
  }

  return null;
}

function cronExpressionTime(expression: string | undefined): string | null {
  const [minute, hour] = expression?.trim().split(/\s+/) ?? [];
  if (!minute || !hour || !/^\d+$/.test(minute) || !/^\d+$/.test(hour)) {
    return null;
  }
  return `${hour}:${minute}`;
}
